/**
 * The draft board — built to turn `8a`, from the canvas.
 *
 * The previous version of this grid was built from the README's prose and was wrong in every
 * cell state at once, which is the whole reason THE CANVAS RULE exists. Everything below comes
 * out of the drawn `8a` region of `FFL Draft Night.dc.html`: the 34px round gutter and ten
 * `minmax(0,1fr)` columns on a 4px gap, the 44px cell, the two-line name with the SURNAME on
 * top, the club chip and bye on the second line, and the pick code right-aligned in monospace.
 *
 * WHAT EACH CELL STATE MEANS, and why it looks the way it does:
 *
 *   made        pale wash in the DRAFTING GM's identity hue. Ten columns of white cells is a
 *               spreadsheet; the tint is what lets a GM find his own row of picks at a glance,
 *               and it is the same hue his badge carries everywhere else in the app.
 *   keeper      the same hue, deeper, inside a near-black 2px frame. A keeper is not a pick
 *               made on the night and the board should not imply somebody chose him at 8pm.
 *   farm        a violet frame instead. Violet is the app's farm colour, and a farm graduate's
 *               cost is computed a different way (2013-III-1 §4).
 *   on the clock  the accent frame, the pulsing dot, the GM's name and a slow sheen across the
 *               cell. This is the single loudest thing on the page by design — the spreadsheet's
 *               central failure is that nobody can tell whose turn it is.
 *   traded away   a green dashed frame naming who holds it now. It sits in the SEAT's column,
 *               because "Rajan's 4.05" is how the room talks about it even after Vishal buys it.
 *   empty       dashed grey and the code alone, in a colour that recedes.
 *
 * ONE DELIBERATE LOSS from the old grid: an empty pick of your own is no longer painted. The
 * drawing shows every open cell identically, and it is right to — the clock, the strip above
 * and the rail all already say when you are up, and marking sixteen of your own cells amber
 * meant the board was loudest about the picks nobody can make yet.
 */

import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { pickCode, type BoardPick } from '../domain/board.js';
import { roundBand, roundBands } from '../domain/picks.js';
import { hueFor } from './GmBadge.js';
import { NflMark, nflInfoOf } from './NflChip.js';
import { PickClock } from './PickClock.js';

/** Name particles that belong with the surname: the drawing renders `St. Brown` on one line. */
const PARTICLES = new Set([
  'st.', 'st', 'van', 'von', 'de', 'del', 'della', 'di', 'da', 'la', 'le', 'du', 'den', 'ter',
]);
const SUFFIXES = new Set(['jr.', 'jr', 'sr.', 'sr', 'ii', 'iii', 'iv', 'v']);

/**
 * Names no rule gets right (Tim, 8 September: "antwaan randle-el should be RANDLE-EL Antwaan").
 *
 * The surname rule works backwards from the end of the name and stops at the first word that is
 * not a particle or a suffix, which is correct for every name in the league's history but this
 * one: `Randle` is neither, so `Antwaan Randle El` drew a bold `El` over a small `Antwaan Randle`.
 * There is no rule to add — `El` is a surname in its own right elsewhere — so it is a list, and a
 * list is honest about being one. The stored name is untouched; the archive, the box scores and
 * every join between them still read `Antwaan Randle El`, and this is the spelling on the cell.
 */
const KNOWN = new Map<string, { last: string; first: string }>([
  ['antwaan randle el', { last: 'Randle-El', first: 'Antwaan' }],
]);

/**
 * Surname on top, given name under it — the drawing's order, and the right one: on a 96px cell
 * the eye is looking for "Chase", not "Ja'Marr". Particles and suffixes travel with the surname
 * so `Amon-Ra St. Brown` reads `St. Brown` / `Amon-Ra` rather than `Brown` / `Amon-Ra St.`
 */
export function splitName(full: string): { last: string; first: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const known = KNOWN.get(parts.join(' ').toLowerCase());
  if (known) return known;
  /*
   * A DEFENCE HAS ONE NAME (Tim, 6 September: "just label the teams as 49ers").
   *
   * The bank stores them as `49ers DEF`, and the ordinary rule takes the last token for the
   * surname — so the board drew a bold `DEF` over a small `49ers`, thirty-two times, and every
   * defence on it looked like the same player. The position tag on the row already says DEF;
   * the name is the club. The stored name is untouched, because it is what every pick, keeper
   * and archive row in the league's history is written against.
   */
  if (parts.length > 1 && /^(def|dst|d\/st)$/i.test(parts[parts.length - 1]!)) {
    return { last: parts.slice(0, -1).join(' '), first: '' };
  }
  if (parts.length <= 1) return { last: parts[0] ?? '', first: '' };

  let end = parts.length;
  while (end > 1 && SUFFIXES.has(parts[end - 1]!.toLowerCase())) end -= 1;
  let start = end - 1;
  while (start > 1 && PARTICLES.has(parts[start - 1]!.toLowerCase())) start -= 1;

  return {
    last: parts.slice(start).join(' '),
    first: parts.slice(0, start).join(' '),
  };
}

/**
 * The surname without its suffix, for places too narrow to carry one — the 9a team card gives a
 * name about 62px and `Harrison Jr.` does not fit in it, while `Harrison` does. The suffix is
 * never the disambiguating part of a name on a fantasy roster; the full name stays on the row's
 * tooltip either way.
 */
export function shortSurname(last: string): string {
  const parts = last.split(/\s+/).filter(Boolean);
  if (parts.length > 1 && SUFFIXES.has(parts[parts.length - 1]!.toLowerCase())) {
    return parts.slice(0, -1).join(' ');
  }
  return last;
}

/** Which of the drawn six a cell is. Order matters: a farm keeper is a keeper first. */
export type CellState = 'farm' | 'keeper' | 'made' | 'clock' | 'gone' | 'empty';

export function cellState(p: BoardPick, onClock: boolean): CellState {
  if (p.player) return p.farm ? 'farm' : p.keeper ? 'keeper' : 'made';
  if (onClock) return 'clock';
  return p.owner.toUpperCase() !== p.seat.toUpperCase() ? 'gone' : 'empty';
}

interface Props {
  board: readonly BoardPick[];
  order: readonly string[];
  rounds: number;
  /**
   * THE ROUND THE ROOM IS IN, for a board that is not one height all the way down (wall variant
   * C). Given, every row carries how far it is from that round, capped at three — the stylesheet
   * turns that into a height, and the band rail is built from the same distances so the brackets
   * still line up with the rows they name. Null anywhere the board is a table.
   */
  focusRound?: number | null;
  /**
   * THE ROUND THE ROOM IS IN, for every variant (Tim, 8 September: "make the picked and unpicked
   * colorwash backgrounds of the active round a bit brighter ... highlight the number cell of the
   * active round and dim the round number of completed rounds").
   *
   * Not the same question as `focusRound`, which only C asks: that one decides how TALL a row is
   * drawn, and it is null on A and B because they draw every row the same height. Where the room
   * is, is worth saying on all three — a wall read from twelve feet wants the eye taken to one
   * row without being told which by a caption.
   */
  activeRound?: number | null;
  /**
   * The pixels the elastic bands have to fill between them, when there are elastic bands. The
   * wall knows what its canvas can spare; the grid knows how the rounds fall into bands.
   */
  fillTo?: number | null;
  clock: BoardPick | null;
  /** The manager looking at the screen — his column heading takes the accent. */
  me?: string;
  onPick: (overall: number) => void;
  /** What this actor is permitted to type into; a commissioner gets everything. */
  canEnterPick: (p: BoardPick) => boolean;
  /**
   * Cells whose clock ran out and which are STILL ENTERABLE — the draft moved past them, the man
   * kept his pick. Drawn as on-the-clock but amber, because "he is up" and "he is late and we
   * carried on without him" are different things and the room needs to tell them apart.
   */
  lapsed?: ReadonlySet<number>;
  /** When the current pick went on the clock, for the lapsed cell's elapsed figure. */
  lapsedSince?: string | null;
  /** The allowance on the round being played, so the on-the-clock cell can count down (17a). */
  clockSeconds?: number | null;
  /** When each open pick's clock started, by overall — the late cell counts from its own start. */
  startedAt?: ReadonlyMap<number, string>;
  /**
   * The instant the cells' clocks are measured against, when it is not the time of day: the
   * moment the room stopped. A frozen board is the board as it stood when Pause was pressed.
   */
  clockNow?: number | null;
  /** Cells a trade in flight touches: the small dashed "in a trade" mark (Pass 17). */
  inTrade?: ReadonlySet<number>;
  /** Keepers moved by their GM: overall -> the sentence on the MOVED pill's tooltip (Pass 17). */
  moved?: ReadonlyMap<number, string>;
  /**
   * Keepers held under the franchise tag — the green `T` badge (17a rev C). A tag is a different
   * fact from a keeper: it is the one band-limited right in the rulebook (2025 P05 §3) and the
   * board is where the room checks who spent theirs.
   */
  tagged?: ReadonlySet<number>;
  onUndo?: (p: BoardPick) => void;
  /**
   * Moving a keeper to a different pick, when the actor is allowed to (2015-III-7 — earlier
   * always, later never). The grid asks rather than decides: it knows which cells glow and which
   * are handles, and nothing about what makes a round legal.
   *
   * Both a drag AND a click, deliberately. HTML drag-and-drop does not exist on a phone, and the
   * one screen most likely to be used from a phone on draft night is this one.
   */
  drag?: {
    /** The player being carried, if any. */
    held: string | null;
    canGrab: (p: BoardPick) => boolean;
    canDrop: (p: BoardPick) => boolean;
    onGrab: (p: BoardPick | null) => void;
    onDrop: (p: BoardPick) => void;
  };
}

export function BoardGrid(
  { board, order, rounds, clock, me, onPick, canEnterPick, lapsed, lapsedSince, clockSeconds,
    startedAt, clockNow, inTrade, moved, tagged, onUndo, drag,
    focusRound = null, activeRound = null, fillTo = null }: Props,
) {
  const teams = order.length;
  /* Redraw once when Archivo lands, so the measured name widths are that face's and not a fallback's. */
  useNameWidths();
  const rows = Array.from({ length: rounds }, (_, i) => i + 1);
  /** 0 for the round in play, 1 and 2 for its neighbours, 3 for everything further out. */
  const dist = (r: number) => (focusRound ? Math.min(3, Math.abs(r - focusRound)) : null);
  /*
   * AND THE BANDS STRETCH TO FILL THE SCREEN (Tim, 8 September: "board C doesn't use as much
   * vertical space as it can — compare with B").
   *
   * The four heights were sized for the WORST distribution — a middle round on the clock, which
   * puts eleven rows in the compact band. On round 1 only three rows are anywhere but compact,
   * and the same numbers leave three hundred pixels of empty canvas under the board. So the
   * heights are a SHAPE rather than a size: they keep their proportions and scale up until the
   * rows add up to the budget. `k` never goes below 1, and the budget was measured against the
   * worst distribution, so a board that fits at 1x fits at k.
   */
  const scale = (() => {
    if (!focusRound || !fillTo) return 1;
    const base = [102, 80, 58, 39];
    let need = 0;
    for (let r = 1; r <= rounds; r += 1) need += base[Math.min(3, Math.abs(r - focusRound))]!;
    return need > 0 ? Math.max(1, Math.min(1.9, fillTo / need)) : 1;
  })();
  /*
   * A ROUND IS DONE WHEN EVERY CELL IN IT HAS SOMEBODY IN IT — derived rather than stored, because
   * the board already knows and a second copy of "how far are we" is a second thing to keep in
   * step. A round nobody sits in (every pick traded away, which cannot happen but the arithmetic
   * should not assume it) is not done, it is empty.
   */
  const doneRounds = useMemo(() => {
    const seen = new Map<number, { all: number; made: number }>();
    for (const p of board) {
      const n = seen.get(p.round) ?? { all: 0, made: 0 };
      n.all += 1;
      if (p.player) n.made += 1;
      seen.set(p.round, n);
    }
    const out = new Set<number>();
    for (const [r, n] of seen) if (n.all > 0 && n.all === n.made) out.add(r);
    return out;
  }, [board]);
  /** How far a row is from the round in play, for the wash. Null where nothing is in play. */
  const off = (r: number) => (activeRound ? Math.abs(r - activeRound) : null);
  /*
   * WHERE THE MAGNIFIED BLOCK BEGINS AND ENDS (Tim, 8 September: "draw a faint thin line where the
   * magnified rows start end without adjusting the layout or spacing"). C draws rounds within two
   * of the one in play at their own heights and everything beyond at the smallest; the seam is
   * where that changes, and it is invisible on a board whose rows already differ in height. The
   * rule is drawn from the row's own pseudo-element, absolutely placed, so it costs no space.
   */
  const magnified = (r: number) => focusRound !== null && Math.min(3, Math.abs(r - focusRound)) <= 2;
  const first = rows[0];
  const last = rows[rows.length - 1];
  const edge = (r: number): string | undefined => {
    if (!magnified(r)) return undefined;
    const top = r === first || !magnified(r - 1);
    const bot = r === last || !magnified(r + 1);
    return top && bot ? 'both' : top ? 'top' : bot ? 'bot' : undefined;
  };

  /* The rail is a grid of the same rows, so it reads the same heights rather than assuming one. */
  const railRows = focusRound
    ? `var(--bg-head) ${rows.map((r) => `var(--bg-cell-${dist(r)})`).join(' ')}`
    : `var(--bg-head) repeat(${rounds}, var(--bg-cell))`;

  return (
    <div className="dboardwrap"
      {...(focusRound ? { style: { '--bg-scale': scale } as CSSProperties } : {})}>
      <div className="dboardrow">
        {/*
          THE BANDS, DOWN THE SIDE (Tim, 3 September). Three brackets, one per band of the Grid:
          rounds 1–2 cannot be kept at all; 3–9 is where the franchise tag may be used (2025 P05
          §3); 10 onward is keeper territory with the sleeper reward (2017-II-2 §2). The rail is
          its own grid on the board's row metrics — `--bg-head`, `--bg-cell`, `--bg-gap`, which
          the cells and the header also read — so a bracket spans exactly the rows it names, on
          the page and on the wall alike.
        */}
        <div className="dbrail" aria-label="Keeper bands by round"
          style={{ gridTemplateRows: railRows }}>
          {roundBands(rounds).filter((b) => b.band !== 'unkeepable').map((b) => (
            <div key={b.band} className={`dbband ${b.band}`} title={b.title}
              style={{ gridRow: `${b.from + 1} / ${b.to + 2}` }}>
              <span>{b.label}</span>
            </div>
          ))}
        </div>
      <div className="dboard">
        <div className="dbrow dbhead">
          <div className="dbrdhead">RD</div>
          {order.map((g) => (
            <div className={`dbgm ${me && g.toUpperCase() === me.toUpperCase() ? 'me' : ''}`}
              key={g} data-seat={order.indexOf(g) + 1}>
              <span className="nm">{managerShort(g)}</span>
            </div>
          ))}
        </div>

        {rows.map((r) => (
          /*
           * THE BAND IS A WASH ON THE ROW, not a tint on each cell (CD's cell spec, §0). One
           * rectangle behind ten cells and the round label reads as a band; ten separately
           * tinted cells read as ten cells that happen to match, and the gaps between them cut
           * the band into stripes.
           */
          <div className={`dbrow band-${roundBand(r)}`} key={r}
            {...(focusRound ? { 'data-d': dist(r) } : {})}
            {...(off(r) !== null && off(r)! <= 1 ? { 'data-act': off(r) } : {})}
            {...(edge(r) ? { 'data-edge': edge(r) } : {})}>
            {/* The snake, stated rather than counted: odd rounds run left, even rounds right. */}
            <div className={`dbrd${r === activeRound ? ' on' : ''}${doneRounds.has(r) ? ' done' : ''}`}>
              <b>{r}</b>
              {/*
                A ROUND THAT IS OVER SAYS SO (Tim, 8 September: "as rounds are completed, replace
                the arrow with a circle checkmark"). The arrow is the snake — which way this round
                runs — and it is only worth reading on a round somebody is still picking in. Once
                the last seat is filled the direction is a fact about the past, and the thing the
                room wants from that row is that it is done. Drawn rather than typed, because ✓ is
                a different width in every font the wall might fall back to and the round numbers
                have to stay in a column.
              */}
              <i aria-hidden="true">
                {doneRounds.has(r)
                  ? (
                    <svg className="rddone" viewBox="0 0 16 16" role="presentation">
                      <circle cx="8" cy="8" r="6.9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M4.7 8.2l2.3 2.3 4.4-4.7" fill="none" stroke="currentColor"
                        strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )
                  : (r % 2 === 1 ? '→' : '←')}
              </i>
            </div>
            {order.map((seat) => {
              const p = board.find((x) => x.round === r && x.seat === seat);
              if (!p) return <div className="dbcell empty" key={seat} />;
              return (
                <Cell key={seat} p={p} teams={teams}
                  state={cellState(p, clock?.overall === p.overall || Boolean(lapsed?.has(p.overall)))}
                  late={Boolean(lapsed?.has(p.overall))}
                  lapsedSince={startedAt?.get(p.overall) ?? lapsedSince}
                  clockSeconds={clockSeconds}
                  clockNow={clockNow}
                  inTrade={Boolean(inTrade?.has(p.overall))}
                  moved={moved?.get(p.overall)}
                  tagged={Boolean(tagged?.has(p.overall))}
                  can={!p.player && canEnterPick(p)}
                  onPick={onPick} onUndo={onUndo} drag={drag} />
              );
            })}
          </div>
        ))}
      </div>

      </div>

      {/*
        The legend is the drawing's, in the drawing's order — every frame the board can wear,
        and the three corner badges that ride on them. `open` is last because it is the state
        that needs no explaining and would otherwise lead.
      */}
      <div className="dblegend">
        <span className="key"><i className="sw keeper" />keeper<b className="kbadge k">K</b></span>
        <span className="key"><i className="sw tagged" />franchise tag<b className="kbadge t">T</b></span>
        <span className="key"><i className="sw farm" />farm graduate<b className="kbadge f">F</b></span>
        <span className="key"><i className="sw clock" />on the clock</span>
        <span className="key"><i className="sw late" />lapsed, still open</span>
        <span className="key"><i className="sw gone" />traded away</span>
        <span className="key"><i className="sw open" />open</span>
      </div>
    </div>
  );
}

function Cell(
  { p, teams, state, can, late, lapsedSince, clockSeconds, clockNow, inTrade, moved, tagged,
    onPick, onUndo, drag }: {
    p: BoardPick; teams: number; state: CellState; can: boolean; late?: boolean;
    lapsedSince?: string | null; clockSeconds?: number | null; clockNow?: number | null;
    inTrade?: boolean; moved?: string; tagged?: boolean;
    onPick: (overall: number) => void; onUndo?: (p: BoardPick) => void; drag?: Props['drag'];
  },
) {
  const code = pickCode(p, teams);
  /*
   * Tinted by whoever DRAFTS the cell, not whoever sits in the column. A traded pick that has
   * been used belongs, visually, to the man who used it — that is what makes "find my picks"
   * work on a board with eight trades in it.
   */
  const hue = hueFor(p.owner);
  /*
   * ONE WASH, NOT THREE (CD's cell spec §1). A keeper used to sit a shade deeper than a made
   * pick, which meant the wash was answering two questions at once — whose player, and how he
   * got here. The frame and the corner tag answer the second one; the wash is identity alone.
   */
  const tint = p.player ? `hsl(${hue} 50% 92.5%)` : undefined;

  const traded = p.owner.toUpperCase() !== p.seat.toUpperCase();
  const why = moved ? moved
    : p.player
    ? `${p.player} — ${managerShort(p.owner)}, ${code}${traded ? ` (${managerShort(p.seat)}’s pick)` : ''}`
    : traded ? `${managerShort(p.seat)}’s ${code}, now ${managerShort(p.owner)}’s`
      : `${code} — ${managerShort(p.owner)}`;

  const body = p.player ? <Made p={p} code={code} tagged={tagged} />
    : state === 'clock'
      ? <OnClock p={p} code={code} late={late} since={lapsedSince} seconds={clockSeconds}
        now={clockNow} />
      : state === 'gone' ? <Gone p={p} code={code} />
        : inTrade ? (
          /*
           * A cell a trade in flight touches: the code where it always was, and the mark under
           * it. The code leads because the cell is still an open pick — the trade is a thing
           * happening TO it, not what it is.
           */
          <>
            <span className="bot"><span className="code">{code}</span></span>
            <span className="top intrade"><i aria-hidden="true">⇄</i> in a trade</span>
          </>
        )
          : <span className="bot"><span className="code">{code}</span></span>;

  /*
   * CARRYING CHANGES WHAT EVERY CELL MEANS. While a keeper is in hand the board stops being a
   * list of picks and becomes a set of destinations, so the cells that are not destinations
   * recede and the ones that are glow. Without that the manager is guessing at which of a
   * hundred and sixty cells will accept the drop, and 2015-III-7 is not obvious by eye.
   */
  const carrying = Boolean(drag?.held);
  const grab = !carrying && (drag?.canGrab(p) ?? false);
  const holding = carrying && Boolean(p.player) && drag?.held === p.player;
  /*
   * The cell he is standing on is NOT a target, even though dropping him back on it is legal and
   * harmless. Drawn as one it looks like a place to move him to, and the one thing the carrying
   * state has to be unambiguous about is which cell he is currently in.
   */
  const target = carrying && !holding && (drag?.canDrop(p) ?? false);

  const cls = `dbcell ${state}${can ? ' can' : ''}${late ? ' late' : ''}${inTrade && !p.player ? ' intrade' : ''}${moved ? ' moved' : ''}`
    + `${grab ? ' grabbable' : ''}${target ? ' droptarget' : ''}${holding ? ' inhand' : ''}`
    + `${carrying && !target && !holding ? ' aside' : ''}`;

  /** Drop plumbing, identical on both branches below — a cell is a target or it is not. */
  const dropProps = target
    ? {
      onDragOver: (e: React.DragEvent) => e.preventDefault(),
      onDrop: (e: React.DragEvent) => { e.preventDefault(); drag!.onDrop(p); },
    }
    : {};

  /*
   * A CLICKABLE CELL IS A BUTTON — but a button cannot contain a button, so the clear × could
   * never appear on a cell somebody was allowed to type into. That is exactly the commissioner's
   * cell: he may enter any pick, so every filled cell was a button, so he never saw an × on the
   * board at all and had to go hunting for the one control that cleared the last pick.
   *
   * So a filled cell is a div with its own affordances instead: click the body to re-enter it,
   * press the × to clear it. Both reachable, neither nested.
   */
  if (can && !p.player) {
    return (
      <button type="button" className={cls} style={{ background: tint, ['--hue' as string]: hue } as React.CSSProperties}
        title={target ? `Charge ${drag!.held} to ${code}` : why}
        {...dropProps}
        onClick={() => (target ? drag!.onDrop(p) : onPick(p.overall))}>
        {body}
      </button>
    );
  }
  /*
   * An empty cell somebody may NOT type into is still a legal home for his own keeper — a GM who
   * has already used his 3rd can still be charged a keeper to a 4th he holds. So the drop
   * handlers hang off this branch as well, not only off the enterable one.
   */
  return (
    <div className={`${cls}${can && p.player ? ' recan' : ''}`} style={{ background: tint, ['--hue' as string]: hue } as React.CSSProperties}
      /*
        DRAGGABLE WHILE HELD, TOO. Click-to-pick-up and drag are the same gesture from the user's
        side, and `grab` goes false the moment something is carried — so a cell picked up by
        clicking could not then be dragged, which is exactly what somebody does when the click
        did not make it obvious where to drop him. Holding him and dragging him is one motion.
      */
      draggable={grab || holding}
      onDragStart={grab || holding ? () => drag!.onGrab(p) : undefined}
      /*
        NO onDragEnd RELEASE. It fires on a cancelled drag AND after a successful drop, and on the
        successful path it raced the drop handler and put the keeper down before it landed.
        Release happens on drop, on Esc, or on a second click.
      */
      onClick={
        target ? () => drag!.onDrop(p)
          : grab ? () => drag!.onGrab(p)
            : holding ? () => drag!.onGrab(null)
              : undefined
      }
      {...dropProps}
      title={
        target ? `Charge ${drag!.held} to ${code}`
          : holding ? `${p.player} — drop him on any pick at or before ${code}, or click to stop`
            : grab ? `${why} — drag to change which pick he costs`
              : can && p.player && !p.keeper ? `${why} — click to enter a different player` : why
      }>
      {can && p.player && !p.keeper ? (
        <button type="button" className="cellopen" title={`Re-enter ${code}`}
          onClick={() => onPick(p.overall)}>{body}</button>
      ) : body}
      {/*
        NO × ON A KEEPER. He is not a pick somebody made; he is a declared keeper occupying the
        pick he costs, and clearing him from the board would leave the declaration saying one
        thing and the board another. Changing a keeper is the declaration screen's job.
      */}
      {p.player && !p.keeper && onUndo && (
        <button type="button" className="undo" title={`Clear ${p.player} from ${code}`}
          onClick={(e) => { e.stopPropagation(); onUndo(p); }}>×</button>
      )}
    </div>
  );
}

/**
 * How much room the surname needs.
 *
 * The drawing sets one size, 10.5px, and every name in it happens to be short. Real data is not
 * so tidy: at a 91px cell, `Harrison Jr.` and `Smith-Njigba` clip to `Harris…` and `Smith-…`,
 * and on a draft board an ambiguous name is the one thing that cannot happen — `Harris…` is
 * either Harris or Harrison and the room will argue about it. So a long surname steps down a
 * size, and a very long one takes both lines and gives up the given name, which is the part
 * nobody needed to read.
 */
/**
 * HOW WIDE A SURNAME IS, PER POINT OF TYPE (Tim, 8 September: "allow the last name font to adjust
 * to fit the cell when it overruns like HAMPTON and MCBRIDE").
 *
 * `nameFit` below sorts names into three sizes by CHARACTER COUNT, which is a proxy and a poor
 * one in caps: `HAMPTON` and `MCBRIDE` are seven characters like `JACKSON`, and `WILLIAM` — and
 * two of those four fit a 32px cell while the others do not. `IIIIIII` and `MMMMMMM` are the
 * same length and nearly twice the width apart.
 *
 * So the width is measured, once per surname, in the font the board actually draws, and handed
 * to CSS as a ratio: how many pixels wide the name is at 1px of type. The stylesheet then asks
 * for `min(<the band's size>, <the container's width> / ratio)`, which is exact — the name is
 * drawn as large as the band allows and no larger than the cell can hold. No truncation, no
 * tiers, and every size decision stays in the stylesheet where the rest of them are.
 */
const WIDTHS = new Map<string, number>();
let pad: HTMLCanvasElement | null = null;

export function nameWidth(last: string): number {
  const hit = WIDTHS.get(last);
  if (hit !== undefined) return hit;
  /* Node and the tests have no canvas; 0.62em a character is the average for this face. */
  let w = last.length * 0.62;
  try {
    pad ??= document.createElement('canvas');
    const c = pad.getContext('2d');
    if (c) {
      c.font = '800 100px Archivo, ui-sans-serif, system-ui, sans-serif';
      const m = c.measureText(last.toUpperCase()).width / 100;
      if (m > 0) w = m;
    }
  } catch { /* no DOM: keep the estimate */ }
  WIDTHS.set(last, w);
  return w;
}

/**
 * The first measurement can land before Archivo has loaded, and a fallback face's metrics are
 * not this one's. When the real font arrives the cache is dropped and the board redrawn once.
 */
export function useNameWidths(): number {
  const [n, bump] = useState(0);
  useEffect(() => {
    const f = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!f?.ready) return undefined;
    let live = true;
    void f.ready.then(() => { if (live) { WIDTHS.clear(); bump((x) => x + 1); } });
    return () => { live = false; };
  }, []);
  return n;
}

export function nameFit(last: string): 'plain' | 'tight' | 'wrap' {
  /*
   * MEASURE THE SURNAME, NOT THE SUFFIX.
   *
   * `Harrison Jr.` is twelve characters and was tipping into `wrap`, which gives up the given
   * name — so the board showed `Harrison Jr.` alone while every card around it carried a first
   * name. But the thing competing for width is `Harrison`; the suffix is three more characters
   * of small type and was never the problem. Judging the fit on the surname proper keeps
   * `Harrison Jr.` / `Marvin` and still wraps the genuinely long ones like `Smith-Njigba`.
   */
  const bare = last.replace(/\s+(jr\.?|sr\.?|i{2,3}|iv|v)$/i, '');
  /*
   * `wrap` used to give up the given name entirely for a very long surname. CD's cell spec has
   * both lines on every made cell — line 1 the surname, line 2 the given name and the trade
   * block — so the longest names step DOWN a size instead of losing a line.
   */
  if (bare.length >= 11) return 'wrap';
  return bare.length >= 8 ? 'tight' : 'plain';
}

function Made({ p, code, tagged }: { p: BoardPick; code: string; tagged?: boolean }) {
  const { last, first } = splitName(p.player ?? '');
  const fit = nameFit(last);
  // The club and the bye are resolved live from the player index rather than stored on the
  // pick: Waddle is a Bronco now, and a board that remembers him as a Dolphin is worse than
  // one that says nothing.
  const bye = nflInfoOf(p.player ?? '')?.bye ?? p.bye ?? null;
  /*
   * WHOSE PICK IT WAS, WHEN THAT IS NOT WHOSE COLUMN IT IS. The cell sits in the seat's column
   * because "Rajan's 4.05" is how the room talks about it, so a made pick on a traded cell has
   * to name the man who actually used it or the column lies.
   */
  const traded = p.owner.toUpperCase() !== p.seat.toUpperCase();
  return (
    <>
      <span className="top">
        <span className={`pn ${fit}`} title={p.player}
          style={{ '--wu': nameWidth(last) } as CSSProperties}>
          {/*
            THE SURNAME GETS ITS OWN LINE AND NOTHING COMPETES FOR IT. Everything that qualifies
            a cell — who actually holds the pick, and whether the keeper was moved — rides the
            GIVEN-NAME line instead, where the given name is the part that may truncate. A board
            that shortens `SMITH-NJIGBA` to `SMIT…` to make room for a marker has traded the one
            thing the room reads it for.
          */}
          <b>{last}</b>
          {first || traded ? (
            <span className="pnsub">
              {first && <i>{first}</i>}
              {/*
                THE TRADE BLOCK (CD §5). Who holds the pick now, in caps, flush against the
                cell's right edge at the end of the given-name line — the cell stays in the
                ORIGINAL owner's column, because "Rajan's 4.05" is how the room says it, so
                something has to name the man who actually used it.
              */}
              {traded && <span className="viawho">→ {managerShort(p.owner).toUpperCase()}</span>}
            </span>
          ) : null}
        </span>
      </span>
      <span className="bot">
        {p.pos && <span className={`poschip ident pos-${p.pos}`}>{p.pos}</span>}
        <NflMark player={p.player ?? ''} />
        {bye !== null && <span className="bye" title={`Bye week ${bye}`}>{bye}</span>}
        <span className="code">{code}</span>
      </span>
      {/*
        THE CORNER BADGE. One letter in the frame's own colour, top right, because the frame is
        already saying which of the three this is and a word-long pill was eating the name.
        K keeper · T franchise tag · F farm graduate; the legend spells all three out.
      */}
      {p.keeper && (
        <span className={`kbadge ${p.farm ? 'f' : tagged ? 't' : 'k'}`} aria-hidden="true"
          title={p.farm ? 'Farm graduate' : tagged ? 'Held under the franchise tag' : 'Keeper'}>
          {p.farm ? 'F' : tagged ? 'T' : 'K'}
        </span>
      )}
    </>
  );
}

function OnClock(
  { p, code, late, since, seconds, now }: {
    p: BoardPick; code: string; late?: boolean; since?: string | null; seconds?: number | null;
    now?: number | null;
  },
) {
  /*
   * TWO CELLS, NOT ONE WITH A COLOUR SWAP. The man on the clock gets the blue frame, his name
   * and the time he has left; a man the draft has moved past gets a solid red cell with his
   * name and how long he has been late. "He is up" and "we carried on without him" are the two
   * things the room reads the board for during a lapse, and they should not rhyme.
   */
  if (late) {
    return (
      <>
        <span className="lapwho">{managerShort(p.owner)}</span>
        {since && <PickClock now={now ?? undefined} since={since} seconds={null} size="s" skin="light" className="lapclk" />}
        <span className="vh">{code} — his clock ran out; the pick is still his to enter</span>
      </>
    );
  }
  return (
    <>
      <span className="wipe" aria-hidden="true" />
      <span className="onwho"><i className="dot" aria-hidden="true" />{managerShort(p.owner)}</span>
      <PickClock now={now ?? undefined} since={since} seconds={seconds ?? null} size="s" skin="light" className="onclk" />
      <span className="vh">{code}</span>
      {/*
        THE STRIP RUNS THE WAY THE ROUND RUNS. The cap is where the clock is; the chevrons trail
        off in the direction the round is heading, fading as they go — right on an odd round,
        mirrored on an even one. Drawn as one SVG rather than three bordered boxes, because a
        chevron made out of a CSS triangle is a wedge, and four wedges in a row read as a fan.
      */}
      <svg className={`onrun ${p.round % 2 === 1 ? 'fwd' : 'back'}`} viewBox="0 0 46 12"
        width="34" height="9" aria-hidden="true" focusable="false">
        <rect className="cap" x="2" y="2.6" width="10" height="6.8" rx="3.4" />
        <g className="chevs" fill="none" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 2 L20 6 L15 10" opacity=".9" />
          <path d="M23 2 L28 6 L23 10" opacity=".7" />
          <path d="M31 2 L36 6 L31 10" opacity=".45" />
          <path d="M39 2 L44 6 L39 10" opacity=".25" />
        </g>
      </svg>
    </>
  );
}

function Gone({ p, code }: { p: BoardPick; code: string }) {
  return (
    <>
      <span className="top gonewho">
        <span className="gonelab">Traded</span>
        <span className="code">{code}</span>
      </span>
      <span className="bot goneto">
        <i aria-hidden="true">→</i>
        <b>{managerShort(p.owner)}</b>
        <span className="gonefrom">from {managerShort(p.seat)}</span>
      </span>
    </>
  );
}
